import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useAuth } from '../../stateManagers/services/AuthContext';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import CourseChips from '../Subjects/parts/CourseChips';
import AddPracticalMaterialModal from './parts/AddPracticalMaterialModal';
import PracticalMaterialDetailsModal from './parts/PracticalMaterialDetailsModal';
import PracticalMaterialLibrarySidebar, { LibraryMaterial } from './parts/PracticalMaterialLibrarySidebar';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PracticalFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

interface HLSVideo {
  video_id: string;
  original_name: string;
  m3u8_url: string | null;
  size: number;
  status: string;
}

export interface PracticalMaterial {
  id: string;
  course_id: string;
  module_id: string | null;
  chapter_id: string | null;
  scope_type: 'chapter' | 'course';
  institute_id: string;
  branch_id: string | null;
  owner_level: 'institute' | 'branch';
  title: string;
  body: string;
  order: number;
  status: string;
  files: PracticalFile[];
  video_links: string[];
  videos: HLSVideo[];
  files_count: number;
  videos_count: number;
  created_by: string;
  created_by_role: 'super_admin' | 'admin' | 'operator';
  created_at: string;
}

interface Chapter {
  id: string;
  title: string;
  order: number;
  practical_materials: PracticalMaterial[];
  practical_materials_count: number;
}

interface Module {
  id: string;
  title: string;
  order: number;
  chapters: Chapter[];
  chapters_count: number;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

// ── Permission helper ───────────────────────────────────────────────────────
// Super Admin panel: no restrictions — super_admin can manage everything.
export function canManagePracticalMaterial(
  _material: PracticalMaterial,
  _user: { id: string; role: string; institute_id?: string; branch_id?: string }
): boolean {
  return true;
}

// ── Component ──────────────────────────────────────────────────────────────────

const PracticalMaterials: React.FC = () => {
  const { user } = useAuth();
  const toast    = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});

  const [modules,          setModules]          = useState<Module[]>([]);
  const [generalMaterials, setGeneralMaterials] = useState<PracticalMaterial[]>([]);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');

  const [showAddModal,     setShowAddModal]     = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null); // null = general
  const [selectedMaterial,  setSelectedMaterial]  = useState<PracticalMaterial | null>(null);

  const [showLibrarySidebar,   setShowLibrarySidebar]   = useState(false);
  const [reuseTargetChapterId, setReuseTargetChapterId] = useState<string | null>(null);
  const [reuseSource,          setReuseSource]          = useState<LibraryMaterial | null>(null);

  const [deleteDialog, setDeleteDialog] = useState<{ visible: boolean; id: string; title: string }>({
    visible: false, id: '', title: '',
  });

  useEffect(() => {
    if (selectedCourseId) {
      fetchCourseStructure();
    } else {
      setModules([]);
      setGeneralMaterials([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  const fetchCourseStructure = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/practical-materials/course/${selectedCourseId}/structure`);
      if (response.data.success) {
        setModules(response.data.data.modules || []);
        setGeneralMaterials(response.data.data.general_materials || []);
      } else {
        setError('Failed to load practical materials');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load practical materials');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect     = (course: Course) => setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
  const handleAddGeneral       = () => { setSelectedChapterId(null); setSelectedMaterial(null); setShowAddModal(true); };
  const handleAddForChapter    = (chapterId: string) => { setSelectedChapterId(chapterId); setSelectedMaterial(null); setShowAddModal(true); };
  const handleView             = (material: PracticalMaterial) => { setSelectedMaterial(material); setShowDetailsModal(true); };
  const handleEdit             = (material: PracticalMaterial) => {
    setSelectedMaterial(material);
    setSelectedChapterId(material.chapter_id);
    setShowAddModal(true);
  };
  const handleDelete           = (id: string, title: string) => setDeleteDialog({ visible: true, id, title });

  const handleOpenLibrary = (chapterId: string | null) => {
    setReuseTargetChapterId(chapterId);
    setShowLibrarySidebar(true);
  };

  const handleReuseSelect = (material: LibraryMaterial) => {
    setReuseSource(material);
    setSelectedChapterId(reuseTargetChapterId);
    setSelectedMaterial(null);
    setShowLibrarySidebar(false);
    setShowAddModal(true);
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/practical-materials/${deleteDialog.id}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Practical material deleted successfully', life: 3000 });
      fetchCourseStructure();
      setDeleteDialog({ visible: false, id: '', title: '' });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to delete', life: 4000 });
    }
  };

  const getMaterialMenuItems = (material: PracticalMaterial) => {
    return [
      { label: 'View Details', icon: 'pi pi-eye', command: () => handleView(material) },
      { label: 'Edit',   icon: 'pi pi-pencil', command: () => handleEdit(material) },
      { label: 'Delete', icon: 'pi pi-trash',  command: () => handleDelete(material.id, material.title) },
    ];
  };

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
      </div>
    );
  }

  const totalMaterials =
    generalMaterials.length + modules.reduce((sum, m) => sum + m.chapters.reduce((s, c) => s + c.practical_materials_count, 0), 0);

  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, id: '', title: '' })}
        onConfirm={confirmDelete}
        title="Delete Practical Material"
        message={`Are you sure you want to delete "${deleteDialog.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)', borderRadius: '20px', padding: '32px', marginBottom: '24px', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)' }}>
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                Practical Material Management
              </div>
              <h1 style={{ margin: '0 0 8px 0', fontSize: '1.7rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>Practical Files</h1>
              <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                Manage practical materials across institutes and branches
              </p>
              <div style={{ marginTop: '12px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                <i className="pi pi-file" style={{ marginRight: '6px' }}></i>
                {selectedCourseId ? `${totalMaterials} practical material${totalMaterials !== 1 ? 's' : ''}` : 'Select a course'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <GlobalButton
                label="Reuse Existing Material"
                variant="secondary"
                icon="pi pi-copy"
                onClick={() => handleOpenLibrary(null)}
                disabled={!selectedCourseId}
              />
              <GlobalButton label="Add General Material" variant="success" icon="pi pi-plus" onClick={handleAddGeneral} disabled={!selectedCourseId} />
            </div>
          </div>
        </div>
      </div>

      {/* Course Filter */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <i className="pi pi-filter" style={{ color: '#0f3d3e', fontSize: '1rem' }}></i>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a' }}>Select Course</h3>
          {selectedCourseId && (
            <button onClick={() => setSelectedCourseId('')} style={{ marginLeft: 'auto', padding: '6px 12px', background: 'transparent', border: '1px solid #e0e0e0', borderRadius: '8px', color: '#666', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <i className="pi pi-times" style={{ fontSize: '0.75rem' }} /> Clear Selection
            </button>
          )}
        </div>
        <CourseChips selectedCourseId={selectedCourseId} onCourseSelect={handleCourseSelect} showSearch={false} />
      </div>

      {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />}

      {/* Content */}
      {!selectedCourseId ? (
        <EmptyState icon="pi pi-book" title="Select a Course" subtitle="Click on a course chip above to view and manage practical materials" />
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
            <p style={{ marginTop: '16px', color: '#0f3d3e', fontSize: '0.95rem' }}>Loading practical materials...</p>
          </div>
        </div>
      ) : (
        <>
          {/* General (course-level) materials */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <i className="pi pi-globe" style={{ color: '#0f3d3e' }} />
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a' }}>
                General Materials ({generalMaterials.length})
              </h3>
              <span style={{ fontSize: '0.75rem', color: '#999' }}>— not tied to a specific chapter</span>
            </div>
            {generalMaterials.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: '#999', margin: 0 }}>No general practical materials for this course yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {generalMaterials.map((material) => (
                  <MaterialRow
                    key={material.id}
                    material={material}
                    positionLabel={`G.${material.order}`}
                    menuRefs={menuRefs}
                    menuItems={getMaterialMenuItems(material)}
                    onView={() => handleView(material)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Modules > Chapters > chapter-scoped materials (read-only tree; only materials are managed here) */}
          {modules.length === 0 ? (
            <EmptyState icon="pi pi-sitemap" title="No modules found" subtitle="Ask a super admin to create modules/chapters for this course first" />
          ) : (
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }}>
              {modules.map((module) => (
                <ModuleBlock
                  key={module.id}
                  module={module}
                  menuRefs={menuRefs}
                  materialMenuItems={getMaterialMenuItems}
                  onAddForChapter={handleAddForChapter}
                  onReuseForChapter={(chapterId) => handleOpenLibrary(chapterId)}
                  onViewMaterial={handleView}
                />
              ))}
            </div>
          )}
        </>
      )}

      {selectedMaterial && (
        <PracticalMaterialDetailsModal
          visible={showDetailsModal}
          material={selectedMaterial}
          canManage={true}
          onHide={() => setShowDetailsModal(false)}
          onEdit={() => { setShowDetailsModal(false); setShowAddModal(true); }}
        />
      )}

      <AddPracticalMaterialModal
        visible={showAddModal}
        courseId={selectedCourseId}
        chapterId={selectedChapterId}
        editingMaterial={selectedMaterial}
        reuseSource={reuseSource}
        onHide={() => { setShowAddModal(false); setSelectedMaterial(null); setSelectedChapterId(null); setReuseSource(null); }}
        onSuccess={() => {
          toast.current?.show({ severity: 'success', summary: 'Success', detail: selectedMaterial ? 'Practical material updated!' : 'Practical material created!', life: 3000 });
          fetchCourseStructure();
          setShowAddModal(false);
          setSelectedMaterial(null);
          setSelectedChapterId(null);
          setReuseSource(null);
        }}
      />

      <PracticalMaterialLibrarySidebar
        visible={showLibrarySidebar}
        onHide={() => setShowLibrarySidebar(false)}
        onReuse={handleReuseSelect}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ icon: string; title: string; subtitle: string; children?: React.ReactNode }> = ({ icon, title, subtitle, children }) => (
  <div style={{ padding: '80px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
    <i className={icon} style={{ fontSize: '3.5rem', color: '#ddd', marginBottom: '16px', display: 'block' }}></i>
    <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>{title}</h3>
    <p style={{ margin: '0 0 20px 0', color: '#999', fontSize: '0.9rem' }}>{subtitle}</p>
    {children}
  </div>
);

const OwnerBadge: React.FC<{ material: PracticalMaterial }> = ({ material }) => {
  const isInstitute = material.owner_level === 'institute';
  return (
    <span
      style={{
        fontSize: '0.65rem', fontWeight: '700', padding: '2px 8px', borderRadius: '10px',
        background: isInstitute ? '#fef3c7' : '#e0f2fe',
        color: isInstitute ? '#92400e' : '#075985',
        textTransform: 'uppercase', letterSpacing: '0.3px',
      }}
    >
      {isInstitute ? 'Institute' : 'Branch'}
    </span>
  );
};

interface ModuleBlockProps {
  module: Module;
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  materialMenuItems: (m: PracticalMaterial) => any[];
  onAddForChapter: (chapterId: string) => void;
  onReuseForChapter: (chapterId: string) => void;
  onViewMaterial: (m: PracticalMaterial) => void;
}

const ModuleBlock: React.FC<ModuleBlockProps> = ({ module, materialMenuItems, onAddForChapter, onReuseForChapter, onViewMaterial, menuRefs }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', border: '2px solid #e0e0e0', borderRadius: '16px', padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.1rem', fontWeight: '700', color: '#d4f17e' }}>
          {module.order}
        </div>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1a1a1a' }}>{module.title}</h3>
      </div>

      {module.chapters.length > 0 && (
        <div style={{ marginTop: '16px', paddingLeft: '60px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {module.chapters.map((chapter) => (
            <div key={chapter.id} style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: '600', color: '#0f3d3e' }}>
                    {module.order}.{chapter.order}
                  </div>
                  <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a' }}>{chapter.title}</h4>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => onReuseForChapter(chapter.id)}
                    style={{ padding: '6px 12px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', color: '#4338ca', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    <i className="pi pi-copy" style={{ marginRight: '4px' }} /> Reuse
                  </button>
                  <button
                    onClick={() => onAddForChapter(chapter.id)}
                    style={{ padding: '6px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#16a34a', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    <i className="pi pi-plus" style={{ marginRight: '4px' }} /> Add Material
                  </button>
                </div>
              </div>

              {chapter.practical_materials.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {chapter.practical_materials.map((material) => (
                    <MaterialRow
                      key={material.id}
                      material={material}
                      positionLabel={`${module.order}.${chapter.order}.${material.order}`}
                      menuRefs={menuRefs}
                      menuItems={materialMenuItems(material)}
                      onView={() => onViewMaterial(material)}
                    />
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.8rem', color: '#999', margin: 0 }}>No practical materials yet.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

interface MaterialRowProps {
  material: PracticalMaterial;
  positionLabel: string;
  menuRefs: React.MutableRefObject<{ [key: string]: Menu | null }>;
  menuItems: any[];
  onView: () => void;
}

const MaterialRow: React.FC<MaterialRowProps> = ({ material, positionLabel, menuRefs, menuItems, onView }) => {
  const readyVideos      = material.videos?.filter((v) => v.status === 'ready').length      ?? 0;
  const processingVideos = material.videos?.filter((v) => v.status === 'processing').length ?? 0;
  const menuKey = `practical-material-${material.id}`;

  return (
    <div
      onClick={onView}
      style={{ padding: '12px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#e8f4f8', border: '1px solid #b8dde8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.7rem', fontWeight: '700', color: '#0f3d3e' }}>
          {positionLabel}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1a1a1a' }}>{material.title}</span>
            <OwnerBadge material={material} />
          </div>
          <div style={{ fontSize: '0.75rem', color: '#999', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {material.files_count > 0 && (
              <span><i className="pi pi-paperclip" style={{ marginRight: '4px' }}></i>{material.files_count} file{material.files_count !== 1 ? 's' : ''}</span>
            )}
            {readyVideos > 0 && (
              <span style={{ color: '#16a34a', fontWeight: '600' }}><i className="pi pi-play-circle" style={{ marginRight: '4px' }}></i>{readyVideos} video{readyVideos !== 1 ? 's' : ''}</span>
            )}
            {processingVideos > 0 && (
              <span style={{ color: '#d97706' }}><i className="pi pi-spin pi-spinner" style={{ marginRight: '4px', fontSize: '0.7rem' }}></i>{processingVideos} processing</span>
            )}
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); menuRefs.current[menuKey]?.toggle(e); }}
          style={{ padding: '6px 10px', background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '6px', color: '#0f3d3e', cursor: 'pointer', flexShrink: 0 }}
        >
          <i className="pi pi-ellipsis-v" style={{ fontSize: '0.75rem' }}></i>
        </button>
        <Menu model={menuItems} popup ref={(el) => { menuRefs.current[menuKey] = el; }} />
      </div>
    </div>
  );
};

export default PracticalMaterials;